/**
 * Los tiles «Número de WhatsApp» y «Disponibles hoy» del Resumen. Puros.
 *
 * En twilio el número sale SÓLO de `senderE164`: un `sender` sin número es un resto del modo
 * simulado (el paso a real no lo borraba y el Resumen decía «Simulado · Estado: ONLINE · Calidad:
 * Alta» y 250 personas de cupo sin tener número). Y el estado y la calidad del sender se muestran
 * sólo con la cuenta verificada en este modo: antes de verificar, el sender guardado puede ser el del
 * simulador. En simulado, como siempre: el nombre del sender simulado o su número.
 *
 * Tampoco hay número en twilio con la cuenta todavía del modo simulado (`modo: "simulado"`, o el flag
 * `verificadaEnOtroModo`): el «Verificar» simulado guarda `senderE164 = +5491100000000` y nada lo borra
 * hasta que se conecta la cuenta real, ni la limpieza de restos. Ese número no es de la marca, y su
 * cupo de 250 tampoco.
 */

import type { CuentaPublica, ModoTransporte, Resumen } from "../types";
import { calidadInfo, ESTADO_NUMERO } from "./textos";
import { textoCupo } from "./formato";
import { cuentaDelModo } from "./conexion";

type ResumenTiles = Pick<Resumen, "modoTransporte" | "cuenta">;

type CuentaConNumero = { senderE164?: string } & Partial<Pick<CuentaPublica, "modo" | "verificadaEnOtroModo">>;

const e164De = (r: ResumenTiles) => String(r.cuenta?.senderE164 || "").trim();

/**
 * En twilio, ¿la cuenta no tiene un número real? Sin `senderE164`, o con el que dejó el simulador (la
 * cuenta sigue en `modo: "simulado"` o viene `verificadaEnOtroModo`). Sin número no hay número ni
 * cupo que mostrar: el backend puede mandar el sender y el número viejos. La usan el Resumen y
 * Configuración → Envío. En simulado, nunca: es la demo.
 */
export function sinNumeroEnTwilio(modo: ModoTransporte | null | undefined, c: CuentaConNumero | null | undefined): boolean {
  if (modo !== "twilio") return false;
  if (!String(c?.senderE164 || "").trim()) return true;
  return c?.modo === "simulado" || c?.verificadaEnOtroModo === true;
}

/** Valor y texto chico del tile «Número de WhatsApp». `valor: null` → el tile muestra «—» y `vacio`. */
export function tileNumero(r: ResumenTiles): { valor: string | null; sub?: string; vacio: string } {
  if (!r.cuenta) return { valor: null, vacio: "Sin conectar" };
  if (sinNumeroEnTwilio(r.modoTransporte, r.cuenta)) return { valor: null, vacio: "Falta registrar el número de WhatsApp" };
  const e164 = e164De(r);
  if (r.modoTransporte === "twilio" && !cuentaDelModo(r.cuenta, r.modoTransporte)) {
    return { valor: e164, sub: "Sin verificar: conectá la cuenta para ver su estado", vacio: "Sin conectar" };
  }
  const s = r.cuenta.sender;
  const valor = s?.nombreVisible || e164 || null;
  if (!s) return { valor, sub: "Sin datos del número todavía", vacio: "Sin conectar" };
  const estado = String(s.estado || "").trim();
  const estadoTexto = estado ? ESTADO_NUMERO[estado.toUpperCase()] ?? estado : "sin dato";
  return { valor, sub: `Estado: ${estadoTexto} · Calidad: ${calidadInfo(s.calidad, s.calidadRaw).label}`, vacio: "Sin conectar" };
}

/** Tile «Disponibles hoy»: el de Meta (`textoCupo`), salvo en twilio sin número, donde todavía no hay cupo. */
export function tileCupo(r: Pick<Resumen, "modoTransporte" | "cuenta" | "cupo24h">): { valor: string | null; sub?: string; vacio?: string; meterPct: number | null } {
  if (sinNumeroEnTwilio(r.modoTransporte, r.cuenta)) return { valor: null, vacio: "Se habilita con el número (Meta arranca con 250)", meterPct: null };
  const c = textoCupo(r.cupo24h);
  return { valor: c.valor, sub: c.sub, meterPct: c.meterPct };
}
